<template>
  <v-card
    class="pa-4 mb-7"
    :class="{
      aliceBlue: props.isSiteLocationEditMode || props.isSiteLocationAddMode,
    }"
  >
    <v-row>
      <v-col cols="7">
        <v-select
          :items="props.siteLocationsForMonitor"
          :item-value="(item) => item.id"
          :model-value="props.MonitorStatus.siteLocationId"
          @update:model-value="emit('update:siteLocationId', $event)"
          item-title="siteLocationName"
          hint="What location is this monitor installed in?"
          persistent-hint
          variant="outlined"
          density="compact"
          color="primary"
          v-if="!props.isSiteLocationEditMode && !props.isSiteLocationAddMode"
        >
          <template v-slot:label>
            <span class="text-primary text-caption font-weight-bold">
              Site Location
            </span>
          </template>
        </v-select>
        <v-text-field
          ref="siteLocationNameEditMode"
          :model-value="props.siteLocation.siteLocationName"
          @update:model-value="emit('update:siteLocationName', $event)"
          required
          maxlength="50"
          hint="Edit this site location's name"
          persistent-hint
          append-icon="mdi-square-edit-outline"
          class="text--enabled"
          variant="outlined"
          density="compact"
          color="primary"
          v-if="props.isSiteLocationEditMode && !props.isSiteLocationAddMode"
        >
          <template v-slot:label>
            <span class="text-primary text-caption font-weight-bold">
              Site Location - EDIT SITE LOCATION NAME
            </span>
          </template>
        </v-text-field>
        <v-text-field
          ref="siteLocationNameAddMode"
          v-model="newSiteLocationName"
          required
          maxlength="50"
          hint="Add a new site location's name"
          persistent-hint
          append-icon="mdi-pencil-plus"
          class="text--enabled"
          variant="outlined"
          density="compact"
          color="primary"
          v-if="!props.isSiteLocationEditMode && props.isSiteLocationAddMode"
        >
          <template v-slot:label>
            <span class="text-primary text-caption font-weight-bold">
              Site Location - ADD SITE LOCATION NAME
            </span>
          </template>
        </v-text-field>
      </v-col>
      <!-- Add/Edit Buttons -->
      <v-col>
        <v-btn
          v-if="props.isSiteLocationEditMode"
          class="rounded-2 mr-2"
          color="primary"
          variant="flat"
          :loading="isEditLoading"
          :disabled="!props.isMonitorDirty"
          @click="handleSaveEditSiteLocation"
        >
          <div
            class="text-subtitle-2 text-white text-truncate font-weight-bold px-1"
          >
            Save
          </div>
        </v-btn>
        <v-tooltip location="bottom" v-else>
          <template v-slot:activator="{ props: activatorProps }">
            <v-btn
              class="rounded-2 mr-2"
              color="primary"
              v-bind="activatorProps"
              variant="flat"
              :loading="isEditLoading"
              :disabled="isAddButtonDisabled"
              @click="handleAddButtonClick"
            >
              <div
                class="text-subtitle-2 text-white text-truncate font-weight-bold px-1"
              >
                Add
              </div>
            </v-btn>
          </template>
          <span> Add New Site Location </span>
        </v-tooltip>

        <v-btn
          class="rounded-2"
          :color="getSiteLocationButtonColor"
          variant="flat"
          @click="handleSiteLocationButtonClick"
          :disabled="props.isMonitorDirty && !props.isSiteLocationEditMode"
        >
          <div
            class="text-subtitle-2 text-white text-truncate font-weight-bold px-1"
          >
            {{ getSiteLocationButtonText }}
          </div>
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>
<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import API from '@/Core.Service.Domain/Monitors/Monitors.Common/API/';
import type {
  IAginovaMonitor,
  ISiteLocation,
} from '@/Core.Service.Domain/Monitors/Aginova/types';

interface Props {
  MonitorStatus: IAginovaMonitor;
  isMonitorDirty: boolean;
  siteLocation: ISiteLocation;
  isSiteLocationEditMode: boolean;
  isSiteLocationAddMode: boolean;
  siteLocationsForMonitor: ISiteLocation[];
}

const props = defineProps<Props>();
const emit = defineEmits([
  'site-location-edit-mode',
  'site-location-add-mode',
  'cancel-site-location-edit-mode',
  'init',
  'update:siteLocationName',
  'update:siteLocationId',
]);

const siteLocationNameEditMode = ref<HTMLInputElement | null>(null);
const siteLocationNameAddMode = ref<HTMLInputElement | null>(null);
const isEditLoading = ref(false);
const newSiteLocationName = ref('');
const newSiteLocationId = ref('');

const getSiteLocationButtonText = computed(() => {
  if (props.isSiteLocationEditMode || props.isSiteLocationAddMode) {
    return props.isSiteLocationEditMode ? 'Cancel Edition' : 'Cancel Add';
  } else {
    return 'Edit Name';
  }
});

const isAddButtonDisabled = computed(() => {
  return (
    (props.isMonitorDirty && !props.isSiteLocationEditMode) ||
    (props.isSiteLocationAddMode && newSiteLocationName.value === '')
  );
});

const getSiteLocationButtonColor = computed(() => {
  return props.isSiteLocationEditMode || props.isSiteLocationAddMode
    ? 'warning'
    : 'primary';
});

const handleSaveEditSiteLocation = async () => {
  isEditLoading.value = true;
  try {
    const updatedSiteLocation: ISiteLocation = { ...props.siteLocation };
    updatedSiteLocation.siteLocationId = updatedSiteLocation.id;
    delete updatedSiteLocation.id;

    await API.updateMonitorSiteLocation(updatedSiteLocation);
  } catch (error) {
    return Promise.reject(error);
  } finally {
    emit('site-location-edit-mode', false);
    isEditLoading.value = false;
    emit('init');
  }
};

const handleSaveAddSiteLocation = async () => {
  isEditLoading.value = true;
  try {
    const newSiteLocation: ISiteLocation = { ...props.siteLocation };
    newSiteLocation.siteLocationName = newSiteLocationName.value;
    delete newSiteLocation.id;

    const { id } = await API.createMonitorSiteLocation(newSiteLocation);
    newSiteLocationId.value = id;
  } catch (error) {
    return Promise.reject(error);
  } finally {
    emit('site-location-add-mode', false);
    isEditLoading.value = false;
    emit('init');
  }
};

const handleEnterSiteLocationAddMode = () => {
  emit('site-location-add-mode', true);
  nextTick(() => {
    siteLocationNameAddMode.value?.focus();
  });
};

const handleEnterSiteLocationEditMode = () => {
  emit('site-location-edit-mode', true);
  nextTick(() => {
    siteLocationNameEditMode.value?.focus();
  });
};

const handleAddButtonClick = () => {
  if (props.isSiteLocationAddMode) {
    handleSaveAddSiteLocation();
  } else {
    handleEnterSiteLocationAddMode();
  }
};

const handleSiteLocationButtonClick = () => {
  if (props.isSiteLocationEditMode || props.isSiteLocationAddMode) {
    emit('cancel-site-location-edit-mode');
  } else {
    handleEnterSiteLocationEditMode();
  }
};
</script>
<style lang="scss" scoped>
:deep(.v-select) {
  .v-select__selections {
    .v-select__selection {
      color: $primary !important;
    }
  }
}

:deep(.v-list) {
  .v-list-item__title {
    color: $primary !important;
  }
}
</style>
